/**
 * useEmailShares — read/write the caller's per-relationship email shares.
 *
 * Phase G.7.9 (sacred/email-sharing.md):
 *   Mutual share at invite-time (auto-trigger). Frozen at share moment.
 *   Owners may revoke or re-share with their current email.
 *
 * Two surfaces:
 *   - VIEWER side (what emails do I see?): byOwnerContactId Map for fast
 *     inline lookup from LineageList rows.
 *   - OWNER side (who has my email?): list of shares I've made + revoke +
 *     bulk re-share actions.
 *
 * Privacy: RLS limits SELECT — viewer reads shares-with-them; owner reads
 * shares-they-made. Same row can be visible to both.
 *
 * Anonymity invariants (sacred/anonymous-invitations.md §VIII + MX2 amendment):
 *   1. INSERT trigger `core.create_mutual_email_share_on_invite` skips the
 *      inviter→invitee row when invitationAnonymous=true at insert time.
 *   2. RPC `core.toggle_invitation_anonymity` SYNCS the inviter→invitee
 *      row in lockstep with the boolean: cloaking revokes, un-cloaking
 *      re-creates with current email.
 *   3. RPC `core.share_my_email_with_recognized` excludes pending invitees
 *      AND anonymously-cloaked pairs from bulk-share.
 *   4. DEFENSIVE: BEFORE INSERT trigger `refuse_anonymous_email_share_on_insert`
 *      refuses any leaky INSERT at the DB level. The doctrine is enforced
 *      structurally, not just by convention.
 *
 * @scale-marker stage-a — at ~1k relationships, paginate the owner-side
 *   list. Viewer-side map can stay unpaginated until ~10k since it's a
 *   key-value lookup keyed by ownerContactId.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface EmailShareRow {
  id: string;
  ownerContactId: string;
  viewerContactId: string;
  sharedEmail: string;
  sharedAt: string;
  revokedAt: string | null;
  revokedReason: string | null;
}

const SHARE_COLUMNS =
  'id, ownerContactId, viewerContactId, sharedEmail, sharedAt, revokedAt, revokedReason';

export function useEmailShares() {
  const { contact } = useAuth();
  const [iSee, setISee] = useState<EmailShareRow[]>([]);
  const [iShared, setIShared] = useState<EmailShareRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!contact) {
      setISee([]);
      setIShared([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);

      // Two parallel queries:
      // 1. Where I'm the viewer (emails shared WITH me)
      // 2. Where I'm the owner (emails I've shared with others)
      const [{ data: viewerData, error: viewerErr }, { data: ownerData, error: ownerErr }] =
        await Promise.all([
          supabase
            .schema('core')
            .from('OrchistEmailShare')
            .select(SHARE_COLUMNS)
            .eq('viewerContactId', contact.id)
            .is('revokedAt', null),
          supabase
            .schema('core')
            .from('OrchistEmailShare')
            .select(SHARE_COLUMNS)
            .eq('ownerContactId', contact.id),
        ]);

      if (cancelled) return;

      if (viewerErr || ownerErr) {
        setError(new Error(viewerErr?.message ?? ownerErr?.message ?? 'Could not read shares'));
        setLoading(false);
        return;
      }

      setISee((viewerData ?? []) as EmailShareRow[]);
      setIShared((ownerData ?? []) as EmailShareRow[]);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [contact, refreshKey]);

  /** Map<ownerContactId, sharedEmail> — used by inline LineageList overlay */
  const byOwnerContactId = useMemo(() => {
    const map = new Map<string, EmailShareRow>();
    for (const r of iSee) map.set(r.ownerContactId, r);
    return map;
  }, [iSee]);

  /** Active shares I've made (not revoked) */
  const activeIShared = useMemo(() => iShared.filter((r) => !r.revokedAt), [iShared]);

  const shareWith = useCallback(async (viewerContactId: string) => {
    const { error: err } = await supabase
      .schema('core')
      .rpc('share_my_email_with', { p_viewer_contact_id: viewerContactId });
    if (err) throw new Error(err.message);
    refresh();
  }, [refresh]);

  const revokeShare = useCallback(
    async (viewerContactId: string, reason: string | null = null) => {
      const { error: err } = await supabase
        .schema('core')
        .rpc('revoke_my_email_share', {
          p_viewer_contact_id: viewerContactId,
          p_reason: reason,
        });
      if (err) throw new Error(err.message);
      refresh();
    },
    [refresh]
  );

  const shareWithAllRecognized = useCallback(async () => {
    const { data, error: err } = await supabase
      .schema('core')
      .rpc('share_my_email_with_recognized');
    if (err) throw new Error(err.message);
    refresh();
    return data as { sharedWithCount: number; currentEmail: string };
  }, [refresh]);

  return {
    loading,
    error,
    iSee,
    iShared,
    activeIShared,
    byOwnerContactId,
    refresh,
    shareWith,
    revokeShare,
    shareWithAllRecognized,
  };
}
